"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useRef, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "./AuthProvider";

export function AuthButton({ mobile }: { mobile?: boolean }) {
  const { user, loading, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div
        className={`${mobile ? "w-full h-10" : "w-24 h-9"} bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse`}
      />
    );
  }

  if (!user) {
    return (
      <Link
        href={`/signup?redirect=${encodeURIComponent(pathname)}`}
        className={`inline-flex items-center justify-center bg-accent text-white font-medium rounded-lg hover:bg-accent-hover transition-colors text-sm ${
          mobile ? "w-full py-2.5 px-4" : "py-2 px-4 whitespace-nowrap"
        }`}
      >
        {mobile ? "空室通知を受け取る" : "空室をお知らせ"}
      </Link>
    );
  }

  // ログイン済み
  const displayName =
    user.user_metadata?.full_name ||
    user.email?.split("@")[0] ||
    "ユーザー";
  const avatarUrl = user.user_metadata?.avatar_url;
  const initial = displayName.charAt(0).toUpperCase();

  if (mobile) {
    return (
      <div className="space-y-2">
        <Link
          href="/mypage"
          className="block w-full py-2.5 px-4 text-center bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          マイページ
        </Link>
        <button
          onClick={signOut}
          className="block w-full py-2.5 px-4 text-center text-sm text-muted hover:text-foreground transition-colors"
        >
          ログアウト
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full hover:opacity-80 transition-opacity"
        aria-label="ユーザーメニュー"
      >
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt={displayName}
            width={32}
            height={32}
            className="rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
            {initial}
          </div>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-50">
          <div className="px-4 py-2 text-xs text-muted border-b border-gray-100 dark:border-gray-700 truncate">
            {user.email}
          </div>
          <Link
            href="/mypage"
            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={() => setOpen(false)}
          >
            マイページ
          </Link>
          <button
            onClick={() => {
              setOpen(false);
              signOut();
            }}
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            ログアウト
          </button>
        </div>
      )}
    </div>
  );
}
